import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Trash, Plus, MagnifyingGlass } from "@phosphor-icons/react";

const sel = "h-9 rounded-sm border border-input bg-background px-2 text-sm";
const EMPTY = { match_type: "product_id", match_value: "", rep_id: "", product_key: "$47 Website", label: "", active: true };

function AddRow({ reps, initial, onSaved }) {
  const [f, setF] = useState(initial || EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      await api.post("/admin/votel/product-map", { ...f, rep_id: f.rep_id || null });
      toast.success("Mapping added"); setF(EMPTY); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-6" data-testid="map-add-row">
      <select className={sel} value={f.match_type} onChange={set("match_type")} data-testid="map-match-type">
        <option value="product_id">Product</option>
        <option value="pricing_tier_id">Price option (optional extra)</option>
      </select>
      <Input className="md:col-span-2 font-mono text-xs" placeholder="product_id (or pricing_tier_id)" value={f.match_value} onChange={set("match_value")} data-testid="map-match-value" />
      <select className={sel} value={f.rep_id} onChange={set("rep_id")} data-testid="map-rep-select">
        <option value="">House (no rep)</option>
        {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
      </select>
      <Input placeholder="Product key e.g. $47 Website" value={f.product_key} onChange={set("product_key")} data-testid="map-product-key" />
      <Button size="sm" className="h-9 gap-1" onClick={save} data-testid="map-add-btn"><Plus size={14} /> Add</Button>
    </div>
  );
}

export const ProductMap = ({ rows, reps, onChange }) => {
  const [options, setOptions] = useState(null);
  const lookup = async () => {
    try {
      const r = await api.get("/admin/votel/price-options");
      if (!r.data.reachable) toast.error("Votel not reachable from here — enter the ID manually");
      setOptions(r.data.options);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const update = async (row, patch) => {
    try { await api.put(`/admin/votel/product-map/${row.id}`, { ...row, ...patch }); onChange(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete mapping "${row.label || row.match_value}"?`)) return;
    try { await api.delete(`/admin/votel/product-map/${row.id}`); onChange(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-sm border border-border bg-card p-5" data-testid="product-map-card">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="font-heading font-semibold">Rep product map</h3>
          <p className="text-xs text-muted-foreground">Keyed on Votel product (one product per rep). Product key feeds the rep's commission rule. Rep = House or no match → house revenue, never a guessed rep.</p>
        </div>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={lookup} data-testid="price-options-lookup-btn">
          <MagnifyingGlass size={15} /> Look up from Votel
        </Button>
      </div>
      <table className="mb-4 w-full text-sm" data-testid="product-map-table">
        <thead><tr className="border-b border-border">
          {["Match", "Value", "Rep", "Product key", "Label", "Active", ""].map((c) => <th key={c} className="px-2 py-2 text-left overline">{c}</th>)}
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={7} className="px-2 py-6 text-center text-muted-foreground">No mappings — every charge is logged as unattributed.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-border" data-testid={`map-row-${r.id}`}>
              <td className="px-2 py-2 font-mono text-xs">{r.match_type === "pricing_tier_id" ? "price option" : "product"}</td>
              <td className="px-2 py-2 font-mono text-xs">{r.match_value || <span className="text-[hsl(var(--warning))]">awaiting ID</span>}</td>
              <td className="px-2 py-2">{r.rep_name}</td>
              <td className="px-2 py-2 font-mono text-xs">{r.product_key}</td>
              <td className="px-2 py-2 text-xs text-muted-foreground">{r.label}</td>
              <td className="px-2 py-2"><Switch checked={r.active} onCheckedChange={(v) => update(r, { active: v })} data-testid={`map-active-${r.id}`} /></td>
              <td className="px-2 py-2 text-right"><Button variant="ghost" size="sm" onClick={() => remove(r)} data-testid={`map-delete-${r.id}`}><Trash size={14} /></Button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <AddRow reps={reps} onSaved={onChange} />
      {options && (
        <div className="mt-4" data-testid="price-options-list">
          <div className="overline mb-2">Products seen on Votel transactions</div>
          {options.length === 0 && <div className="text-xs text-muted-foreground">None found.</div>}
          {options.map((o) => (
            <div key={`${o.product_id}-${o.pricing_tier_id}`} className="flex items-center justify-between border-b border-border py-1.5 font-mono text-xs">
              <span>{o.product_name} · product {o.product_id} · tier {o.pricing_tier_id} · {o.charges} charge(s)</span>
              <Button variant="ghost" size="sm" onClick={() => navigator.clipboard?.writeText(o.product_id || "")}>Copy product ID</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
